import React from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@artstorefronts/ui';
import { MessageCircle, UserPlus } from 'lucide-react';
import {
  ProfileHoverCardProvider,
  profileCardName,
  useDeferredConnect,
  type ProfileCardConnectionState,
  type ProfileCardData,
  type ProfileCardResult,
  type ProfileHoverCardAdapter,
  type ProfileLinkArgs,
} from '@arthelper/ui';

import { PROFILE_CARD_QUERY } from '~/graphql/queries';
import { useAuth } from '~/context/auth';
import { useAnalytics } from '~/context/analytics';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useOpenDirectMessage } from '~/hooks/useOpenDirectMessage';
import { ConnectNoteDialog } from '~/components/network/ConnectNoteDialog';
import { connectToaster, useConnectSend, useConnectStrings } from '~/components/network/deferredConnect';
import {
  getConnectButtonLabel,
  isConnectButtonDisabled,
  isIncomingRequest,
} from '~/components/network/connectButtonState';
import { ROUTES_CONFIG } from '../../routes-config';
import { getPublicSiteBaseUrl } from '~/utils/index';
import { FollowButton, useFollowActions } from './FollowButton';

type FollowActions = ReturnType<typeof useFollowActions>;
type ConnectState = Pick<ProfileCardConnectionState, 'userId' | 'connectionId' | 'status' | 'requestedByViewer'>;

/**
 * One follow owner for every card on the page (see useFollowActions): the avatar and the
 * name are separate card instances, and both must read the same override. Pages with their
 * own follow controls join it through useSharedFollowActions, so a follow from the card and
 * one from the page are the same override and the same in-flight guard.
 */
const FollowActionsContext = React.createContext<FollowActions | null>(null);

/**
 * The page's follow owner: the provider's, shared with every hover card on the page, reporting
 * `surface` to analytics. Outside ClientProfileHoverCardProvider it is a local owner, as before
 * the card existed, so a page rendered under another shell still follows.
 */
export function useSharedFollowActions(surface: string) {
  const context = React.useContext(FollowActionsContext);
  // Called unconditionally (rules of hooks) and discarded under the provider; it is the
  // fallback that keeps a page outside ClientProfileHoverCardProvider working.
  const local = useFollowActions(surface);
  const owner = context ?? local;
  const ownerSetFollowing = owner.setFollowing;
  // Stable, like useFollowActions' own: NetworkSuggestedConnections lists it as a useCallback dep.
  const setFollowing = React.useCallback(
    (userId: string, next: boolean) => {
      return ownerSetFollowing(userId, next, surface);
    },
    [ownerSetFollowing, surface]
  );
  return { followOverrides: owner.followOverrides, followPending: owner.followPending, setFollowing };
}

function useClientProfileCard(slug: string, active: boolean): ProfileCardResult {
  const { data, loading, error, refetch } = useQuery(PROFILE_CARD_QUERY, { variables: { slug }, skip: !active });
  return {
    data: data?.profileCard,
    loading,
    error,
    refetch: () => {
      return refetch();
    },
  };
}

/** Client profile links open the town-square profile in a new tab. */
function renderProfileLink({ slug, className, ariaLabel, children }: ProfileLinkArgs) {
  return (
    <a
      href={`${getPublicSiteBaseUrl()}/${slug}`}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
}

/** Mirrors ConnectNotificationButton; a send refetches every card for this member. */
function HoverCardConnectButton({ card }: { card: ProfileCardData }) {
  const { t } = useTranslation('social');
  // Keyed by slug, so a trigger reused for another member never carries this one's request state.
  const [local, setLocal] = React.useState<{ slug: string; state: ConnectState } | undefined>(undefined);
  const localState = local?.slug === card.slug ? local.state : undefined;
  const send = useConnectSend([{ query: PROFILE_CARD_QUERY, variables: { slug: card.slug } }]);
  const strings = useConnectStrings();
  const userId = card.user.asfId;
  const displayName = profileCardName(card);
  const effectiveState = localState ?? card.connectionState;

  const deferred = useDeferredConnect({
    send,
    toaster: connectToaster,
    strings,
    onOptimisticPending: (target) => {
      setLocal({
        slug: card.slug,
        state: { userId: target.userId, connectionId: null, status: 'PENDING', requestedByViewer: true },
      });
    },
    onRevert: () => {
      setLocal(undefined);
    },
    onSettled: (target, { status, connectionId }) => {
      setLocal({ slug: card.slug, state: { userId: target.userId, connectionId, status, requestedByViewer: true } });
    },
  });

  const disabled = deferred.isConnecting(userId) || isConnectButtonDisabled(effectiveState);

  function submitNote(note: string | null) {
    if (!deferred.introTarget) return;
    void deferred.sendIntro(note);
    deferred.clearIntro();
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={`w-full whitespace-nowrap rounded-[8px] gap-1.5 ${
          effectiveState?.status
            ? 'bg-transparent text-gray-700 border border-gray-200'
            : 'bg-transparent text-indigo-600 border border-indigo-300 hover:bg-indigo-600 hover:text-white hover:border-indigo-600'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          deferred.connect({ userId, displayName });
        }}
      >
        <UserPlus className="w-4 h-4" />
        <span>{getConnectButtonLabel(t, effectiveState)}</span>
      </Button>
      <ConnectNoteDialog
        open={deferred.introTarget !== null}
        onOpenChange={() => {
          // The dialog's own close runs onCancel; the hook owns introTarget.
        }}
        displayName={displayName}
        sending={deferred.introSending}
        onCancel={deferred.cancelIntro}
        onSend={submitNote}
      />
    </>
  );
}

function HoverCardMessageButton({ userId, close }: { userId: string; close: () => void }) {
  const { t } = useTranslation('profile');
  const openDirectMessage = useOpenDirectMessage();

  return (
    <div onClickCapture={close}>
      <Button
        variant="outline"
        size="sm"
        className="w-full whitespace-nowrap rounded-[8px] gap-1.5 bg-transparent text-gray-700 border border-gray-200"
        onClick={() => {
          void openDirectMessage(userId, { source: 'hover_card' });
        }}
      >
        <MessageCircle className="w-4 h-4" />
        <span>{t('message-button')}</span>
      </Button>
    </div>
  );
}

function HoverCardActions({ card, close }: { card: ProfileCardData; close: () => void }) {
  const follow = React.useContext(FollowActionsContext);
  const client = useApolloClient();
  const { user } = useAuth();
  const { enabled: connectionsEnabled } = useFeatureFlag('connections_and_dms');
  const userId = card.user.asfId;
  const viewerId = user?.user_id != null && user.user_id !== '' ? String(user.user_id) : undefined;

  // The API returns null viewer state for anonymous and self requests; trust it as well as the id.
  if (!follow || !viewerId || viewerId === userId || card.isFollowedByViewer == null) {
    return null;
  }

  return (
    <div className={`mt-3 grid gap-2 ${connectionsEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
      <FollowButton
        userId={userId}
        isFollowing={follow.followOverrides[userId] ?? !!card.isFollowedByViewer}
        isPending={!!follow.followPending[userId]}
        onToggle={async (id, next) => {
          const ok = await follow.setFollowing(id, next);
          if (!ok) return;
          // The card's follower count is the server's; re-read it rather than guess the delta.
          void client.query({ query: PROFILE_CARD_QUERY, variables: { slug: card.slug }, fetchPolicy: 'network-only' });
        }}
        tone="solid"
        fullWidth
        quiet
      />
      {connectionsEnabled && <SecondaryAction card={card} close={close} />}
    </div>
  );
}

/** The card cannot accept or decline, so an incoming request hands off to the requests page. */
function HoverCardRespondButton({ close }: { close: () => void }) {
  const { t } = useTranslation('social');
  const navigate = useNavigate();

  return (
    <Button
      variant="outline"
      size="sm"
      className="w-full whitespace-nowrap rounded-[8px] gap-1.5 bg-transparent text-indigo-600 border border-indigo-300 hover:bg-indigo-600 hover:text-white hover:border-indigo-600"
      onClick={() => {
        close();
        navigate(ROUTES_CONFIG['/network/requests'].path);
      }}
    >
      <UserPlus className="w-4 h-4" />
      <span>{t('connection.respond-to-request-short')}</span>
    </Button>
  );
}

/** Message only once the server says ACCEPTED — never from the optimistic local PENDING. */
function SecondaryAction({ card, close }: { card: ProfileCardData; close: () => void }) {
  if (card.connectionState?.status === 'ACCEPTED') {
    return <HoverCardMessageButton userId={card.user.asfId} close={close} />;
  }
  if (isIncomingRequest(card.connectionState)) {
    return <HoverCardRespondButton close={close} />;
  }

  return <HoverCardConnectButton card={card} />;
}

function renderActions(card: ProfileCardData, { close }: { close: () => void }) {
  return <HoverCardActions card={card} close={close} />;
}

/** Mount once, inside MessagingDockProvider, so the card's Message button reaches the dock. */
export function ClientProfileHoverCardProvider({ children }: { children: React.ReactNode }) {
  const { enabled } = useFeatureFlag('profile_hover_card');
  const { enabled: showConnectionCount } = useFeatureFlag('connections_and_dms');
  const { t } = useTranslation('social');
  const { amplitude } = useAnalytics();
  const follow = useFollowActions('hover_card');
  // Page-scoped: a route change drops the overrides, so the next page reads fresh server state.
  const { pathname } = useLocation();
  const [followPath, setFollowPath] = React.useState(pathname);
  if (followPath !== pathname) {
    setFollowPath(pathname);
    follow.reset();
  }

  const adapter = React.useMemo<ProfileHoverCardAdapter>(
    () => ({
      enabled,
      useProfileCard: useClientProfileCard,
      renderProfileLink,
      renderActions,
      strings: {
        followers: (count) => t('hover-card.followers', { count }),
        connections: (count) => t('hover-card.connections', { count }),
        loading: t('hover-card.loading'),
        viewProfile: (name) => t('hover-card.view-profile', { name }),
      },
      showConnectionCount,
      onOpened: (card, surface) => {
        amplitude.track('sn_profile_hover_card_opened', { target_user_id: card.user.asfId, surface });
      },
    }),
    [amplitude, enabled, showConnectionCount, t]
  );

  return (
    <FollowActionsContext.Provider value={follow}>
      <ProfileHoverCardProvider adapter={adapter}>{children}</ProfileHoverCardProvider>
    </FollowActionsContext.Provider>
  );
}
